//text-block-3.twig

import React from 'react'
import styled, {css, useTheme} from 'styled-components'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

import {
  ButtonAdapter,
  Content,
  Headline,
  LazyImageAdapter,
  LinkAdapter,
  ListAdapter,
} from '@miro-site/components_utils'
import {Box, Section, Mobile, Desktop} from '@miro-site/core'

import type {Assets} from '@miro-site/components_utils'
import type {BoxProps} from '@miro-site/core'

type HeadlineType = React.ComponentProps<typeof Headline>
type ContentType = React.ComponentProps<typeof Content>

interface Item {
  subtitle?: HeadlineType
  content?: ContentType
  list_type?: 'bullets' | 'numbers'
  list?: {html: React.ReactNode}[]
  button?: React.ComponentProps<typeof ButtonAdapter>
  link?: React.ComponentProps<typeof LinkAdapter>
  image?: Assets
}

interface TextBlockWithScrollingItemsProps {
  rtl?: '0' | '1'
  intro?: {
    align?: 'center' | 'left'
    headline?: HeadlineType
    description?: ContentType
  }
  items: Item[]
  big_image?: boolean
}

const Wrapper = styled(Box).attrs({
  $py: [0, null],
  $px: [20, null],
  $styles: {
    maxWidth: [null, 1120],
  },
})`
  margin: auto;
  position: relative;

  *,
  *:before,
  &:after {
    box-sizing: border-box;
  }
`

interface ElementsProps extends BoxProps {
  $isCentered?: boolean
  $bottomOffset?: 'small' | 'big'
}

const Description = styled(Box).attrs({
  $mt: [16],
  $styles: {
    maxWidth: [null, 550],
  },
})``

const Title = styled(Box).attrs({
  $styles: {
    maxWidth: [null, 740],
  },
})``

const Elements = styled(Box).attrs((props: ElementsProps) => {
  const {$bottomOffset} = props

  let bottomOffset = [null, 80]

  if ($bottomOffset === 'small') {
    bottomOffset = [40, 80]
  }

  if ($bottomOffset === 'big') {
    bottomOffset = [64, 120]
  }

  const styles = {
    $mb: bottomOffset,
    $styles: {
      textAlign: ['left', props.$isCentered ? 'center' : 'left'],
    },
  }

  return styles
})<ElementsProps>`
  ${({$isCentered}) =>
    $isCentered &&
    css`
      ${Title} , ${Description} {
        margin-left: auto;
        margin-right: auto;
      }
    `}
`

const Row = styled(Box).attrs({$my: [null, 0], $mx: [null, -10]})`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  z-index: 3;
`

const ImageContainer = styled.div``

interface ColProps extends BoxProps {
  $isRtl?: boolean
}
const Col = styled(Box).attrs({$pt: [null, 0], $py: [null, 10]})<ColProps>`
  ${({$isRtl, theme}) =>
    $isRtl &&
    css`
      &:nth-of-type(1) {
        order: 2;
      }
      &:nth-of-type(2) {
        order: 1;
      }

      ${ImageContainer} {
        float: right;

        ${theme.mq.small(css`
          float: none;
        `)}
      }
    `}

  &:nth-of-type(1) {
    width: 33.33%;
    flex: 0 0 33.33%;

    ${({theme}) => css`
      ${theme.mq.small(css`
        width: 100%;
        flex: 0 0 100%;
        order: 2;
      `)}
    `};
  }

  &:nth-of-type(2) {
    width: 58.33%;
    flex: 0 0 58.33%;

    ${({theme}) => css`
      ${theme.mq.small(css`
        width: 100%;
        flex: 0 0 100%;
        order: 1;
      `)}
    `};
  }
`

const Item = styled(Box).attrs({
  $pt: [null, 120],
  $styles: {
    opacity: [1, 0.2],
  },
})<BoxProps>`
  transition: opacity 250ms linear;

  & + & {
    ${({theme}) => css`
      ${theme.mq.small(css`
        margin-top: ${theme.multipliers.small(64)};
      `)}
      ${theme.mq.mediumDown(css`
        padding-top: ${theme.multipliers.mediumDown(240)};
      `)}
    ${theme.mq.mediumUp(css`
        padding-top: ${theme.multipliers.mediumUp(240)};
      `)}
    ${theme.mq.large(css`
        padding-top: ${theme.multipliers.large(240)};
      `)}
    `};
  }

  &.is-active {
    opacity: 1;
  }
`

interface ItemSubtitleProps extends BoxProps {
  $isHidden?: boolean
}
const ItemSubtitle = styled(Box).attrs({
  $mb: 16,
})<ItemSubtitleProps>`
  ${({$isHidden}) =>
    $isHidden &&
    css`
      display: none;
    `}
`

interface ImageWrapperProps {
  $isBig?: boolean
}
const ImageWrapper = styled(Box).attrs((props: ImageWrapperProps) => {
  const styles: BoxProps = {$mb: [16, null]}
  if (props.$isBig) {
    styles.$styles = {
      width: [null, 800],
      height: [null, 645],
      maxWidth: ['100%', null],
    }
  } else {
    styles.$styles = {
      width: [null, 645],
      height: [null, 645],
    }
  }

  return styles
})<ImageWrapperProps>`
  position: relative;
`

const Image = styled(Box).attrs({$styles: {borderRadius: 8}})`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 250ms linear;
  overflow: hidden;

  &.is-active {
    opacity: 1;
  }

    ${({theme}) => css`
      ${theme.mq.small(css`
        opacity: 1;
        position: relative;
      `)}
    `};
  }
`

const StartTrigger = styled(Box).attrs({$styles: {height: [null, 120]}})`
  position: absolute;
  left: 0;
  right: 0;
  z-index: -1;
`

function initTextBlock3(
  startTrigger: HTMLElement,
  endTrigger: HTMLElement,
  imageWrapper: Element,
  images: NodeListOf<Element>,
  items: NodeListOf<Element>,
) {
  ScrollTrigger.create({
    trigger: imageWrapper,
    start: function () {
      const startTriggerHeight = startTrigger.getBoundingClientRect().height

      return startTriggerHeight + ' ' + startTriggerHeight * 2
    },
    end: function () {
      const lastItem = items[items.length - 1]
      const lastItemHeight = lastItem.getBoundingClientRect().height

      return 'bottom ' + lastItemHeight
    },
    endTrigger: endTrigger,
    pin: true,
  })

  document.addEventListener('wistia_loaded', function () {
    ScrollTrigger.refresh(true)
  })

  document.addEventListener('lazyloaded', function () {
    ScrollTrigger.refresh(true)
  })

  images.forEach((img, i) => {
    const item = items[i]

    gsap.timeline({
      scrollTrigger: {
        trigger: item,
        start: 'top ' + startTrigger.getBoundingClientRect().height,
        end: 'bottom ' + startTrigger.getBoundingClientRect().height,
        onEnter: function () {
          toggleActive([items[i], images[i]])
        },
        onEnterBack: function () {
          toggleActive([items[i], images[i]])
        },
        onLeave: function () {
          toggleActive([items[i], images[i]], i === items.length - 1)
        },
        onLeaveBack: function () {
          toggleActive([items[i], images[i]], i === 0)
        },
      },
    })
  })

  function toggleActive(elements: [item: Element, image: Element], toggle?: boolean) {
    const enable = typeof toggle !== 'undefined' ? !!toggle : true

    elements.forEach(function (el) {
      if (enable) {
        el.classList.add('is-active')
      } else {
        el.classList.remove('is-active')
      }
    })
  }
}

const TextBlockWithScrollingItems: React.FC<TextBlockWithScrollingItemsProps> = ({
  intro = {},
  items,
  big_image,
  rtl,
}) => {
  const theme = useTheme()
  const animationRef = React.useRef<HTMLElement>(null)
  const animationStartPoint = React.useRef<HTMLDivElement>(null)
  const animationEndPoint = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    let isInitialized = false
    const isMobile = window.innerWidth <= theme.breakpoints.small
    const isAllSet = () =>
      !isMobile || animationRef.current || animationStartPoint.current || animationEndPoint.current
    if (!isAllSet()) {
      return
    }

    init()

    window.addEventListener('resize', init)

    function init(): void {
      if (!isInitialized && isAllSet()) {
        const animatedElement = animationRef.current
        const startPointElement = animationStartPoint.current
        const endPointElement = animationEndPoint.current

        if (!animatedElement || !startPointElement || !endPointElement) {
          return
        }

        const imageWrapper = animatedElement.querySelectorAll(ImageWrapper.toString())[0]
        if (!imageWrapper) return

        const images = animatedElement.querySelectorAll(Image.toString())
        const items = animatedElement.querySelectorAll(Item.toString())

        initTextBlock3(startPointElement, endPointElement, imageWrapper, images, items)
        isInitialized = true
      }

      return window.removeEventListener('resize', init)
    }
  }, [animationRef, animationStartPoint, animationEndPoint])
  const {headline, description} = intro

  return (
    <Section data-place="Text Block Scrolling Section" type="large">
      <Wrapper>
        {(headline?.text || description?.html) && (
          <Elements
            $isCentered={intro.align === 'center'}
            $bottomOffset={headline?.text ? (description?.html ? 'big' : 'small') : undefined}
          >
            {headline?.text && (
              <Headline
                {...headline}
                size={headline.size || 'mid'}
                tag={headline.tag || 'h2'}
              ></Headline>
            )}

            {description?.html && (
              <Description>
                <Content {...description} />
              </Description>
            )}
          </Elements>
        )}
        <Desktop ref={animationRef} $block={true}>
          <Row>
            <Col $isRtl={rtl === '1'}>
              {items?.length && (
                <div>
                  {items.map((item, index) => {
                    const isFirstItem = index === 0
                    const itemSubtitleSize = item.subtitle?.size || 'mid'
                    return (
                      <Item key={index} className={isFirstItem ? 'is-active' : ''}>
                        {item.subtitle && (
                          <ItemSubtitle>
                            {isFirstItem ? (
                              <Headline
                                {...item.subtitle}
                                size={headline ? 'small' : itemSubtitleSize}
                                tag={headline ? 'h3' : itemSubtitleSize == 'small' ? 'h3' : 'h2'}
                              />
                            ) : (
                              <Headline
                                {...item.subtitle}
                                size={item.subtitle.size || 'tiny'}
                                tag={item.subtitle.tag || 'h3'}
                              />
                            )}
                          </ItemSubtitle>
                        )}

                        {item.content && <Content {...item.content} />}

                        {item.list?.length && (
                          <ListAdapter list={item.list} list_type={item.list_type}></ListAdapter>
                        )}

                        {item.button && (
                          <Box $mt={32}>
                            <ButtonAdapter {...item.button} type="transparent" />
                          </Box>
                        )}
                        {item.link && (
                          <Box $mt={24}>
                            <LinkAdapter {...item.link} />
                          </Box>
                        )}

                        {index == items.length - 1 && (
                          <div className="__end" ref={animationEndPoint}></div>
                        )}
                      </Item>
                    )
                  })}
                </div>
              )}
            </Col>
            <Col $isRtl={rtl === '1'}>
              <ImageContainer>
                <ImageWrapper $isBig={big_image}>
                  {items.map((item, index) => {
                    const isFirstItem = index === 0

                    return (
                      item.image && (
                        <Image key={index} className={isFirstItem ? 'is-active' : ''}>
                          <LazyImageAdapter assets={item.image} />
                        </Image>
                      )
                    )
                  })}
                </ImageWrapper>
              </ImageContainer>
            </Col>
            <StartTrigger ref={animationStartPoint} />
          </Row>
        </Desktop>

        <Mobile $block={true}>
          {items.map((item, index) => {
            const itemSubtitleSize = item.subtitle?.size || 'mid'
            return (
              <Item key={index}>
                {item.subtitle && (
                  <ItemSubtitle>
                    {!headline && index === 0 ? (
                      <Headline
                        {...item.subtitle}
                        size={itemSubtitleSize}
                        tag={itemSubtitleSize == 'small' ? 'h3' : 'h2'}
                      />
                    ) : (
                      <Headline
                        {...item.subtitle}
                        size={headline ? 'small' : item.subtitle.size || 'tiny'}
                        tag={item.subtitle.tag || 'h3'}
                      />
                    )}
                  </ItemSubtitle>
                )}

                {item.image && (
                  <ImageContainer>
                    <ImageWrapper $isBig={big_image}>
                      {item.image && (
                        <Image key={index}>
                          <LazyImageAdapter assets={item.image}></LazyImageAdapter>
                        </Image>
                      )}
                    </ImageWrapper>
                  </ImageContainer>
                )}

                {item.content && <Content {...item.content} />}

                {item.list?.length && (
                  <ListAdapter list={item.list} list_type={item.list_type}></ListAdapter>
                )}

                {item.button && (
                  <Box $mt={32}>
                    <ButtonAdapter {...item.button} type="transparent" />
                  </Box>
                )}
                {item.link && (
                  <Box $mt={24}>
                    <LinkAdapter {...item.link} />
                  </Box>
                )}
              </Item>
            )
          })}
        </Mobile>
      </Wrapper>
    </Section>
  )
}

export default TextBlockWithScrollingItems
